import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, Clock, ExternalLink } from 'lucide-react';
import { ProgressStepper, PAYMENT_STEPS } from '../components/ProgressStepper';
import { usePaymentParams } from './PaymentRouter';
import { getSession } from '../lib/api';

type TransactionStatus = 'pending' | 'processing' | 'completed' | 'failed';

interface Transaction {
  sessionId: string;
  status: TransactionStatus;
  amountLocal: number;
  localCurrency: string;
  cryptoAmount: number;
  cryptoCurrency: string;
  network: string;
  walletAddress: string;
  transactionHash?: string;
  completedAt?: string;
  failedReason?: string;
}

export function StatusPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = usePaymentParams();
  const sessionId = searchParams.get('sessionId');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transaction, setTransaction] = useState<Transaction | null>(null);

  useEffect(() => {
    if (sessionId) {
      fetchTransactionStatus();
      // Poll for status updates every 5 seconds until completed/failed
      const interval = setInterval(fetchTransactionStatus, 5000);
      return () => clearInterval(interval);
    } else {
      setError('No session ID provided');
      setLoading(false);
    }
  }, [sessionId]);

  async function fetchTransactionStatus() {
    try {
      const token = localStorage.getItem('pouchpay_token') || undefined;
      const data = await getSession(sessionId!, token);

      // Map the API's session status to a UI status. The provider moves through
      // PAYMENT_PENDING -> PAYOUT_PROCESSING -> COMPLETED (plus PAYMENT_RECEIVED /
      // PROCESSING_PAYOUT variants), so treat all of those as "processing".
      const s: string = data.status || '';
      const status: TransactionStatus =
        s === 'COMPLETED' ? 'completed' :
        s === 'FAILED' ? 'failed' :
        (s === 'PAYOUT_PROCESSING' || s === 'PROCESSING_PAYOUT' ||
         s === 'PAYMENT_RECEIVED' || s === 'PAYMENT_PROCESSING' ||
         s === 'AWAITING_MANUAL_SETTLEMENT') ? 'processing' :
        'pending';

      // Fields live under transaction / paymentInstruction, not top-level.
      const tx = data.transaction;
      const pi = data.paymentInstruction;
      setTransaction({
        sessionId: data.id,
        status,
        amountLocal: tx?.amountLocal ?? pi?.amountLocal ?? 0,
        localCurrency: tx?.localCurrency ?? pi?.localCurrency ?? params?.countryCode ?? '',
        cryptoAmount: tx?.cryptoAmount ?? pi?.cryptoAmount ?? 0,
        cryptoCurrency: tx?.cryptoCurrency ?? pi?.cryptoCurrency ?? params?.asset ?? '',
        network: pi?.cryptoNetwork ?? params?.network ?? '',
        walletAddress: params?.walletAddress ?? '',
        transactionHash: data.txHash,
        completedAt: tx?.settledAt ?? undefined,
        failedReason: undefined
      });

      setLoading(false);
    } catch (err: any) {
      console.error('Fetch status error:', err);
      setError(err.message);
      setLoading(false);
    }
  }

  function handleDone() {
    // Check for successUrl or callbackUrl
    if (params?.successUrl) {
      window.location.href = params.successUrl;
    } else if (params?.callbackUrl) {
      window.location.href = params.callbackUrl;
    } else {
      navigate('/pay');
    }
  }

  function getExplorerUrl(hash: string, network: string): string {
    const explorers: Record<string, string> = {
      TRC20: 'https://tronscan.org/#/transaction/',
      POLYGON: 'https://polygonscan.com/tx/',
      ETHEREUM: 'https://etherscan.io/tx/',
      BASE: 'https://basescan.org/tx/',
      ARBITRUM: 'https://arbiscan.io/tx/',
      OPTIMISM: 'https://optimistic.etherscan.io/tx/',
      AVALANCHE: 'https://snowtrace.io/tx/',
      BNB: 'https://bscscan.com/tx/'
    };

    return (explorers[network] || 'https://etherscan.io/tx/') + hash;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-brand mx-auto mb-4" />
          <p className="text-gray-600">Loading transaction status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/pay')}
            className="px-6 py-2 bg-brand text-white rounded-lg hover:opacity-90 font-medium"
          >
            Start New Payment
          </button>
        </div>
      </div>
    );
  }

  if (!transaction) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Stepper */}
      <ProgressStepper
        steps={PAYMENT_STEPS}
        currentStep="status"
        completedSteps={
          transaction.status === 'completed'
            ? ['amount', 'wallet', 'auth', 'kyc', 'review', 'instructions']
            : ['amount', 'wallet', 'auth', 'kyc', 'review', 'instructions']
        }
      />

      <div className="max-w-2xl mx-auto px-4 pb-8">
        {/* Status card */}
        <div className="bg-white rounded-lg shadow-sm p-8 text-center mb-4">
          {/* Status icon */}
          <div className="mb-6">
            {transaction.status === 'completed' ? (
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
            ) : transaction.status === 'failed' ? (
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
            ) : transaction.status === 'processing' ? (
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                <Clock className="w-10 h-10 text-blue-600 animate-pulse" />
              </div>
            ) : (
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                <Loader2 className="w-10 h-10 text-gray-400 animate-spin" />
              </div>
            )}
          </div>

          {/* Status message */}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {transaction.status === 'completed' ? 'Payment Complete!' :
             transaction.status === 'failed' ? 'Payment Failed' :
             transaction.status === 'processing' ? 'Processing Payment' :
             'Waiting for Payment'}
          </h1>

          <p className="text-gray-600 mb-6">
            {transaction.status === 'completed'
              ? `Your ${transaction.cryptoCurrency} has been sent to your wallet`
              : transaction.status === 'failed'
              ? transaction.failedReason || 'The transaction could not be completed'
              : transaction.status === 'processing'
              ? 'Your payment is being processed. This may take a few minutes.'
              : 'We\'re waiting for your payment to be received'}
          </p>

          {/* Transaction details */}
          <div className="space-y-3 text-left mb-6">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Amount Paid</span>
              <span className="font-semibold text-gray-900">
                {transaction.localCurrency} {transaction.amountLocal.toLocaleString()}
              </span>
            </div>

            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Amount Received</span>
              <span className="font-semibold text-gray-900">
                {transaction.cryptoAmount.toFixed(4)} {transaction.cryptoCurrency}
              </span>
            </div>

            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Network</span>
              <span className="font-semibold text-gray-900">{transaction.network}</span>
            </div>

            {transaction.walletAddress && (
              <div className="flex justify-between items-start p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Wallet</span>
                <span className="font-mono text-sm text-gray-900 text-right break-all">
                  {transaction.walletAddress.slice(0, 10)}...{transaction.walletAddress.slice(-8)}
                </span>
              </div>
            )}

            {transaction.transactionHash && (
              <div className="flex justify-between items-start p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Transaction</span>
                <a
                  href={getExplorerUrl(transaction.transactionHash, transaction.network)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-brand hover:text-brand-600 font-mono text-sm"
                >
                  {transaction.transactionHash.slice(0, 10)}...
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}

            {transaction.completedAt && (
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Completed</span>
                <span className="text-sm text-gray-700">
                  {new Date(transaction.completedAt).toLocaleString()}
                </span>
              </div>
            )}
          </div>

          {/* Action buttons */}
          {transaction.status === 'completed' || transaction.status === 'failed' ? (
            <button
              onClick={handleDone}
              className="w-full py-3 px-6 bg-brand text-white rounded-lg font-semibold hover:opacity-90 transition-opacity"
            >
              {params?.callbackUrl ? 'Back to Website' : 'Done'}
            </button>
          ) : (
            <div className="text-sm text-gray-500">
              This page will update automatically when your payment is processed
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            Powered by{' '}
            <a
              href="https://pouchfinance.xyz"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand hover:text-brand-600 font-medium"
            >
              PouchPay
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
